/**
 * Dimensões e peso usados na cotação de frete.
 *
 * Cada produto pode definir `weight/length/width/height` no admin.
 * Quando algum campo não estiver preenchido, caímos no `DEFAULT_PACKAGE`
 * abaixo (embalagem padrão equivalente a uma camiseta dobrada).
 */

import type { CartItem } from "@/types/cart";
import type { ShippingPackage } from "@/types/shipping";

export const DEFAULT_PACKAGE: Omit<ShippingPackage, "quantity"> = {
  weight: 0.4,   // kg
  length: 25,    // cm
  width: 18,     // cm
  height: 4,     // cm
};

/**
 * Pacotes médios por categoria. Usados quando o produto não tem
 * dimensões/peso preenchidos. Chave = slug da categoria (case-insensitive);
 * também aceitamos correspondência por nome normalizado.
 * Valores são estimativas razoáveis para a embalagem postada (não a peça nua).
 */
export const CATEGORY_PACKAGES: Record<string, Omit<ShippingPackage, "quantity">> = {
  canecas:     { weight: 0.55, length: 12, width: 12, height: 11 }, // caneca + caixa
  camisetas:   { weight: 0.25, length: 25, width: 18, height: 3 },
  cropped:     { weight: 0.22, length: 24, width: 18, height: 3 },
  moletom:     { weight: 0.70, length: 35, width: 28, height: 6 },
  vestidos:    { weight: 0.45, length: 30, width: 22, height: 4 },
  calcas:      { weight: 0.55, length: 32, width: 24, height: 5 },
  bones:       { weight: 0.20, length: 22, width: 18, height: 11 }, // copa rígida
  ecobags:     { weight: 0.20, length: 25, width: 18, height: 2 },
  necessaires: { weight: 0.18, length: 22, width: 16, height: 5 },
  chaveiros:   { weight: 0.05, length: 12, width: 10, height: 2 },
  acessorios:  { weight: 0.15, length: 18, width: 14, height: 4 },
};

const normalize = (v?: string | null) =>
  (v ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

function getCategoryPackage(item: CartItem): { key: string; pkg: Omit<ShippingPackage, "quantity"> } | null {
  const slug = normalize(item.categorySlug);
  if (slug && CATEGORY_PACKAGES[slug]) return { key: slug, pkg: CATEGORY_PACKAGES[slug] };
  const name = normalize(item.categoryName);
  if (!name) return null;
  for (const [key, pkg] of Object.entries(CATEGORY_PACKAGES)) {
    if (normalize(key) === name) return { key, pkg };
  }
  return null;
}

export interface PackageInfo {
  rule: "product" | "category" | "default";
  ruleLabel: string;
  dimensions: Omit<ShippingPackage, "quantity" | "insurance_value">;
}

export function getPackageInfoForItem(item: CartItem): PackageInfo {
  const hasProductDims =
    item.weight && item.weight > 0 &&
    item.length && item.length > 0 &&
    item.width && item.width > 0 &&
    item.height && item.height > 0;

  if (hasProductDims) {
    return {
      rule: "product",
      ruleLabel: "Dimensões do produto",
      dimensions: {
        weight: Number(item.weight),
        length: Number(item.length),
        width: Number(item.width),
        height: Number(item.height),
      },
    };
  }

  const category = getCategoryPackage(item);
  if (category) {
    return {
      rule: "category",
      ruleLabel: `Categoria: ${category.key}`,
      dimensions: { ...category.pkg },
    };
  }

  return {
    rule: "default",
    ruleLabel: "Pacote padrão",
    dimensions: { ...DEFAULT_PACKAGE },
  };
}

export function getPackageForItem(item: CartItem): ShippingPackage {
  const info = getPackageInfoForItem(item);
  return {
    ...info.dimensions,
    insurance_value: item.unitPrice,
    quantity: item.quantity,
  };
}

export function buildPackagesFromCart(items: CartItem[]): ShippingPackage[] {
  return items.map(getPackageForItem);
}